import React, { useEffect, useState } from "react";
import Select from "react-select";
import { BANKS } from "../../Constants";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import {
  clearWithdrawConsultantBalance,
  requestGetConsultant,
  requestWithdrawConsultantBalance,
  useAuthState,
  useConsultantDispatch,
  useConsultantState,
} from "../../Context";
import { Button } from "../../Components/Button";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { useNavigate } from "react-router-dom";
import { formatCurrency, getApiErrorBody } from "../../Utils";

const WITHDRAWAL_FEE = 5000;

export const ConsultantWithdraw = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useConsultantDispatch();
  const {
    consultant,
    loading,
    withdraw,
    withdrawStatus,
    withdrawError,
    withdrawLoading,
  } = useConsultantState();
  const { user } = useAuthState();

  const [formError, setformError] = useState({
    bank: "",
    accountName: "",
    accountNumber: "",
    withdrawalTotal: "",
  });
  const [minWithdrawalError, setminWithdrawalError] = useState("");
  const [maxWithdrawalError, setmaxWithdrawalError] = useState("");
  const [maxWithdrawal, setmaxWithdrawal] = useState(0);
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });

  const [selectedBank, setselectedBank] = useState("");
  const [accountName, setaccountName] = useState("");
  const [accountNumber, setaccountNumber] = useState("");
  const [withdrawalTotal, setwithdrawalTotal] = useState("");

  useEffect(() => {
    if (user.id) {
      requestGetConsultant(dispatch, { userID: user.id });
    }
  }, [dispatch, user.id]);

  useEffect(() => {
    if (consultant.id) {
      const { user, bankAccount } = consultant;

      if (user?.name) {
        setaccountName(user?.name);
      }
      if (bankAccount?.id) {
        setselectedBank(BANKS.find((bank) => bank.value === bankAccount.id));
      }
      if (bankAccount?.number) {
        setselectedBank(
          BANKS.find((bank) => bank.value === bankAccount.number)
        );
      }
    }
  }, [consultant]);

  useEffect(() => {
    if (withdrawStatus === "SUCCESS" && withdraw) {
      setModal({
        visible: true,
        title: t("Success"),
        description: t("Balance withdrawal successful."),
        type: "SUCCESS",
      });
    }
  }, [withdraw, withdrawStatus, t]);

  useEffect(() => {
    const handleErrorMessage = (error) => {
      let errorMessage = "Failed to withdraw balance.";
      const parsedErrorResponse = getApiErrorBody(error);

      if (parsedErrorResponse?.error?.[0]) {
        errorMessage = parsedErrorResponse.error[0];
      }

      setModal({
        visible: true,
        title: t("Error"),
        description: t(errorMessage),
        type: "ALERT",
      });
    };

    if (withdrawError) {
      handleErrorMessage(withdrawError);
    }
  }, [withdrawError, t]);

  useEffect(() => {
    if (consultant?.balance) {
      const maximum =
        consultant.balance - WITHDRAWAL_FEE > 0
          ? consultant.balance - WITHDRAWAL_FEE
          : 0;
      setmaxWithdrawal(maximum);
    }
  }, [consultant]);

  const withdrawAll = () => {
    if (consultant?.balance) {
      const totalBalance =
        consultant.balance - WITHDRAWAL_FEE > 0
          ? consultant.balance - WITHDRAWAL_FEE
          : 0;

      setwithdrawalTotal(totalBalance);
    }
  };

  const handleValueChange = (input) => {
    const currentMaxWithdrawal = formatCurrency(maxWithdrawal);
    const floatValue = input.floatValue || 0;
    setwithdrawalTotal(floatValue);

    if (floatValue < 100000) {
      setminWithdrawalError(t("Minimum withdrawal total Rp100.000"));
    } else if (floatValue > maxWithdrawal) {
      setmaxWithdrawalError(
        t("Maximum withdrawal total") + " " + currentMaxWithdrawal
      );
    } else {
      setminWithdrawalError("");
      setmaxWithdrawalError("");
    }
  };

  const validateForm = () => {
    setformError({
      bank: "",
      accountName: "",
      accountNumber: "",
      withdrawalTotal: "",
    });

    let isValid = true;

    if (!selectedBank) {
      setformError((prevState) => ({
        ...prevState,
        bank: t("Bank is required."),
      }));
      isValid = false;
    }
    if (!accountName) {
      setformError((prevState) => ({
        ...prevState,
        accountName: t("Bank account name is required."),
      }));
      isValid = false;
    }
    if (!accountNumber) {
      setformError((prevState) => ({
        ...prevState,
        accountNumber: t("Account number is required."),
      }));
      isValid = false;
    }
    if (!withdrawalTotal) {
      setformError((prevState) => ({
        ...prevState,
        withdrawalTotal: t("Withdrawal total is required."),
      }));
      isValid = false;
    } else {
      const currentMaxWithdrawal = formatCurrency(maxWithdrawal);
      if (parseFloat(withdrawalTotal) > maxWithdrawal) {
        setformError((prevState) => ({
          ...prevState,
          withdrawalTotal:
            t("Maximum withdrawal total") + " " + currentMaxWithdrawal,
        }));
        isValid = false;
      } else if (parseFloat(withdrawalTotal) < 100000) {
        setformError((prevState) => ({
          ...prevState,
          withdrawalTotal: t("Minimum withdrawal total Rp100.000"),
        }));
        isValid = false;
      }
    }

    return isValid;
  };

  const handleWithdraw = () => {
    if (validateForm()) {
      requestWithdrawConsultantBalance(dispatch, {
        consultantID: consultant.id,
        bank: selectedBank.value,
        accountName,
        accountNumber,
        total: withdrawalTotal,
      });
    }
  };

  const handleModalOpen = () => {
    if (withdrawStatus === "SUCCESS" && withdraw) {
      navigate("/consultant-transaction");
    } else {
      setModal((prevModal) => ({
        ...prevModal,
        visible: false,
      }));
    }
    clearWithdrawConsultantBalance(dispatch);
  };
  function FormSkeleton() {
    return (
      <div className="animate-pulse col-span-12">
        <div className="space-y-6 py-5 lg:px-8 px-3">
          <div className="grid gap-4 sm:grid-cols-2 grid-cols-1">
            <div className="flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <ModalConfirm
        open={modal.visible}
        setOpen={(open) => {
          setModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={modal.title}
        description={modal.description}
        type={modal.type}
        onConfirm={() => handleModalOpen()}
      />
      {withdrawLoading ? (
        <FormSkeleton />
      ) : (
        <div className="grid gap-4 px-4 py-5 sm:p-6 sm:grid-cols-2 grid-cols-1">
          {loading ? (
            <div className="animate-pulse flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Bank")}
              </label>
              <Select
                className="mt-1 text-sm"
                classNamePrefix="select2-selection"
                value={selectedBank}
                onChange={setselectedBank}
                options={BANKS.map((option) => ({
                  label: `${option.shortLabel} (${option.code}) - ${option.abbreviation}`,
                  value: option.value,
                }))}
                placeholder={t("Select Bank")}
                menuPortalTarget={document.body}
                styles={{
                  option: (provided) => ({
                    ...provided,
                    fontSize: "14px",
                    lineHeight: "20px",
                  }),
                }}
              />
              {formError.bank && (
                <p className="mt-1 text-xs text-red-600">{formError.bank}</p>
              )}
            </div>
          )}
          {loading ? (
            <div className="animate-pulse flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Account Name")}
              </label>
              <input
                type="text"
                placeholder={t("Enter account name")}
                value={accountName}
                onChange={(e) => setaccountName(e.target.value)}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
              {formError.accountName && (
                <p className="mt-1 text-xs text-red-600">
                  {formError.accountName}
                </p>
              )}
            </div>
          )}
          {loading ? (
            <div className="animate-pulse flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-10 bg-gray-300 rounded-md"></div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Account Number")}
              </label>
              <NumericFormat
                type="text"
                displayType="input"
                value={accountNumber}
                onChange={(e) => setaccountNumber(e.target.value)}
                placeholder={t("Enter account number")}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
              {formError.accountNumber && (
                <p className="mt-1 text-xs text-red-600">
                  {formError.accountNumber}
                </p>
              )}
            </div>
          )}
          <div>
            <div className="flex gap-2 justify-between">
              <label className="block text-sm font-medium text-gray-700">
                {t("Withdrawal Total")}
              </label>
              <button
                onClick={withdrawAll}
                className="block text-sm font-medium text-blue-600"
              >
                {t("Withdraw All")}
              </button>
            </div>
            <NumericFormat
              type="text"
              displayType="input"
              value={withdrawalTotal}
              thousandSeparator={"."}
              decimalSeparator={","}
              prefix={"Rp"}
              onValueChange={handleValueChange}
              decimalScale={2}
              placeholder={t("Enter a minimum balance withdrawal of Rp100.000")}
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
            {(formError.withdrawalTotal ||
              minWithdrawalError ||
              maxWithdrawalError) && (
              <p className="mt-1 text-xs text-red-600">
                {formError.withdrawalTotal ||
                  minWithdrawalError ||
                  maxWithdrawalError}
              </p>
            )}
          </div>
        </div>
      )}
      <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-4 sm:px-6 text-right">
        <Button
          title={t("Withdraw Balance")}
          disabled={withdrawLoading}
          loading={withdrawLoading}
          onClick={handleWithdraw}
        />
      </div>
    </div>
  );
};
